import { useEffect, useState } from "react";
import { get, post } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Lock, Gavel, FileCheck2, ExternalLink, RefreshCw, Plus, Link2,
  ShieldCheck, AlertTriangle, GitBranch, X,
} from "lucide-react";

const CHECK = {
  current: { c: "bg-[#D1FAE5] text-[#065F46]", label: "Current" },
  watch: { c: "bg-[#EDE9FE] text-[#5B21B6]", label: "Watch" },
  review_due: { c: "bg-[#FEF3C7] text-[#B45309]", label: "Review due" },
  unreachable: { c: "bg-[#FEE2E2] text-[#991B1B]", label: "Unreachable" },
  superseded: { c: "bg-stone-100 text-stone-500", label: "Superseded" },
  conflict_detected: { c: "bg-[#FEE2E2] text-[#991B1B]", label: "Conflict detected" },
  unchecked: { c: "bg-stone-100 text-stone-500", label: "Unchecked" },
};

function CheckBadge({ status }) {
  const s = CHECK[status] || CHECK.unchecked;
  return <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${s.c}`} data-testid={`check-badge-${status}`}>{s.label}</span>;
}

export function GovernedRegister({ isAdmin, onChanged }) {
  const [config, setConfig] = useState(null);
  const [sources, setSources] = useState([]);
  const [sel, setSel] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = () => get("/desk/sources").then((d) => { setSources(d.sources); onChanged?.(); });
  useEffect(() => { get("/desk/config").then(setConfig); load(); }, []);

  const openDetail = async (id) => setSel(await get(`/desk/sources/${id}`));
  const clean = sources.filter((s) => !/^TEST/i.test(s.id) && !/^TEST/i.test(s.title || ""));
  const internal = clean.filter((s) => s.scope === "internal");
  const external = clean.filter((s) => s.scope === "external");

  return (
    <div className="space-y-8" data-testid="governed-register">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-mill-muted">Every policy, procedure and external duty is a governed object with a named owner, version and — for external material — a canonical primary-source link. The system records technical change signals; a human confirms the impact.</p>
        {isAdmin && <Button size="sm" className="bg-mill-moss hover:bg-mill-moss-dark" onClick={() => setAdding(true)} data-testid="add-source"><Plus className="mr-1 h-4 w-4" /> Add object</Button>}
      </div>

      <Chains config={config} onOpen={openDetail} />

      <Group title="Internal Mill objects" items={internal} onOpen={openDetail} testid="register-internal" />
      <Group title="External primary sources" items={external} onOpen={openDetail} testid="register-external" />

      <SourceDetail sel={sel} onClose={() => setSel(null)} onChange={async (fresh) => { setSel(fresh); await load(); }} config={config} isAdmin={isAdmin} />
      {adding && <AddSource config={config} onClose={() => setAdding(false)} onCreated={async () => { setAdding(false); await load(); }} />}
    </div>
  );
}

function Group({ title, items, onOpen, testid }) {
  return (
    <section data-testid={testid}>
      <h3 className="mb-3 font-display text-lg font-semibold text-mill-ink">{title}</h3>
      <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white">
        {items.length === 0 && <p className="p-4 text-sm text-mill-muted">None yet.</p>}
        {items.map((s) => (
          <button key={s.id} onClick={() => onOpen(s.id)} data-testid={`source-row-${s.id}`}
            className="flex w-full items-center justify-between gap-3 border-b border-stone-100 px-4 py-3.5 text-left last:border-0 hover:bg-mill-alt/40">
            <div className="min-w-0">
              <p className="truncate font-medium text-mill-ink">{s.title}</p>
              <p className="truncate text-xs text-mill-muted">{s.obj_type} · {s.owner} · {s.version}{s.source_org ? ` · ${s.source_org}` : ""}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {s.review_overdue && <AlertTriangle className="h-4 w-4 text-[#B45309]" title="Review overdue" />}
              <CheckBadge status={(s.check || {}).status} />
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

function Chains({ config, onOpen }) {
  if (!config?.chains) return null;
  return (
    <section data-testid="obligation-chains">
      <h3 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold text-mill-ink"><GitBranch className="h-5 w-5 text-mill-moss" /> Obligation-to-action chains</h3>
      <div className="space-y-3">
        {config.chains.map((ch) => (
          <div key={ch.id} className={`rounded-2xl border p-4 ${ch.primary ? "border-mill-moss/40 bg-mill-alt/40" : "border-stone-200 bg-white"}`} data-testid={`chain-${ch.id}`}>
            <p className="text-sm font-semibold text-mill-ink">{ch.title}{ch.primary && <span className="ml-2 rounded-full bg-mill-moss px-2 py-0.5 text-[10px] font-medium text-white">main example</span>}</p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {ch.steps.map((st, i) => (
                <span key={i} className="flex items-center gap-1.5">
                  {st.ref
                    ? <button onClick={() => onOpen(st.ref)} className="rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-katlas-blue ring-1 ring-stone-200 hover:underline">{st.label}</button>
                    : <span className="rounded-full bg-white/70 px-2.5 py-1 text-[11px] text-mill-muted ring-1 ring-stone-200">{st.label}</span>}
                  {i < ch.steps.length - 1 && <span className="text-mill-muted">→</span>}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Meta({ label, value }) {
  if (!value) return null;
  return <div><dt className="text-[10px] uppercase tracking-wider text-mill-muted">{label}</dt><dd className="text-sm text-mill-ink">{value}</dd></div>;
}

function SourceDetail({ sel, onClose, onChange, config, isAdmin }) {
  const [busy, setBusy] = useState(false);
  const [rev, setRev] = useState({ reviewer: "", decision: "no_change", note: "", linked_version: "" });
  useEffect(() => { setRev({ reviewer: "", decision: "no_change", note: "", linked_version: "" }); }, [sel?.id]);
  if (!sel) return null;
  const chk = sel.check || {};
  const carops = sel.carops || {};

  const runCheck = async () => {
    setBusy(true);
    try { const fresh = await post(`/desk/sources/${sel.id}/check`, {}); onChange(fresh); toast.success("Source checked."); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Check failed."); }
    setBusy(false);
  };
  const submitReview = async () => {
    if (!rev.reviewer) { toast.error("Enter the reviewer's name."); return; }
    setBusy(true);
    try { const fresh = await post(`/desk/sources/${sel.id}/review`, rev); onChange(fresh); toast.success("Review recorded."); }
    catch { toast.error("Could not record review."); }
    setBusy(false);
  };

  return (
    <Dialog open={!!sel} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto" data-testid="source-detail">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <CheckBadge status={chk.status} />
            <span className="rounded-full bg-mill-alt px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-mill-muted">{sel.scope}</span>
          </div>
          <DialogTitle className="font-display text-xl">{sel.title}</DialogTitle>
        </DialogHeader>

        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Meta label="Type" value={sel.obj_type} />
          <Meta label="Owner" value={sel.owner} />
          <Meta label="Publishing authority" value={sel.publishing_authority} />
          <Meta label="Audience" value={sel.audience} />
          <Meta label="Version" value={sel.version} />
          <Meta label="Status" value={sel.status?.replace(/_/g, " ")} />
          <Meta label="Published" value={sel.published_date} />
          <Meta label="Last reviewed" value={sel.last_reviewed_date} />
          <Meta label="Review due" value={sel.review_due_date} />
          <Meta label="Source org" value={sel.source_org} />
          <Meta label="Required ack." value={sel.required_ack} />
          <Meta label="Approval evidence" value={sel.approval_evidence} />
        </dl>

        {sel.canonical_url && (
          <div className="rounded-xl border border-stone-200 bg-mill-alt/40 p-4" data-testid="source-check-panel">
            <div className="flex items-center justify-between gap-2">
              <a href={sel.canonical_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 truncate text-sm font-medium text-katlas-blue hover:underline">
                <Link2 className="h-4 w-4 shrink-0" /> <span className="truncate">{sel.canonical_url}</span> <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              </a>
              <Button size="sm" variant="outline" onClick={runCheck} disabled={busy} data-testid="check-source-now"><RefreshCw className={`mr-1 h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Check source now</Button>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-3">
              <Meta label="Check status" value={(CHECK[chk.status] || {}).label} />
              <Meta label="HTTP" value={chk.http_status != null ? String(chk.http_status) : ""} />
              <Meta label="Reachable" value={chk.reachable == null ? "" : chk.reachable ? "Yes" : "No"} />
              <Meta label="Redirected" value={chk.redirected == null ? "" : chk.redirected ? "Yes" : "No"} />
              <Meta label="Last checked" value={chk.last_checked ? new Date(chk.last_checked).toLocaleString() : "Never"} />
              <Meta label="Next scheduled" value={chk.next_check ? new Date(chk.next_check).toLocaleDateString() : ""} />
            </div>
            {chk.detail && <p className="mt-2 rounded-lg bg-white px-3 py-2 text-xs text-mill-ink" data-testid="check-detail">{chk.detail}</p>}
            <p className="mt-2 text-[11px] text-mill-muted">Technical signals only. This is not a compliance verdict — a named owner must assess impact and record a decision.</p>
          </div>
        )}

        {/* CAROPS */}
        <div className="grid grid-cols-1 gap-3 rounded-xl border border-stone-200 p-4 sm:grid-cols-3" data-testid="source-carops">
          {[["Custody", Lock, carops.custody, "text-mill-moss"], ["Authority", Gavel, carops.authority, "text-katlas-blue"], ["Evidence", FileCheck2, carops.evidence, "text-[#B45309]"]].map(([t, Icon, txt, tone]) => (
            <div key={t}><p className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] ${tone}`}><Icon className="h-3.5 w-3.5" /> {t}</p><p className="mt-1 text-xs leading-relaxed text-mill-muted">{txt}</p></div>
          ))}
        </div>

        {/* Confirm review */}
        {isAdmin && sel.status !== "superseded" && (
          <div className="rounded-xl border border-stone-200 p-4" data-testid="confirm-review">
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-mill-moss" /><h4 className="font-display text-sm font-semibold text-mill-ink">Confirm review</h4></div>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><Label className="text-xs text-mill-muted">Reviewer</Label><Input value={rev.reviewer} onChange={(e) => setRev({ ...rev, reviewer: e.target.value })} data-testid="review-reviewer" /></div>
              <div><Label className="text-xs text-mill-muted">Decision</Label>
                <select className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={rev.decision} onChange={(e) => setRev({ ...rev, decision: e.target.value })} data-testid="review-decision">
                  {config?.review_decisions.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2"><Label className="text-xs text-mill-muted">Note</Label><Textarea rows={2} value={rev.note} onChange={(e) => setRev({ ...rev, note: e.target.value })} data-testid="review-note" /></div>
              <div><Label className="text-xs text-mill-muted">Resulting / linked version</Label><Input value={rev.linked_version} onChange={(e) => setRev({ ...rev, linked_version: e.target.value })} data-testid="review-version" /></div>
            </div>
            <Button className="mt-3 bg-mill-moss hover:bg-mill-moss-dark" onClick={submitReview} disabled={busy} data-testid="review-submit">Record review decision</Button>
          </div>
        )}

        {sel.reviews?.length > 0 && (
          <div data-testid="review-history">
            <p className="text-xs font-semibold uppercase tracking-wider text-mill-muted">Review history</p>
            <div className="mt-2 space-y-1.5">
              {sel.reviews.map((r) => (
                <div key={r.id} className="rounded-lg bg-mill-alt/40 px-3 py-2 text-xs text-mill-ink">
                  <b>{r.reviewer}</b> · {r.decision.replace(/_/g, " ")} · {new Date(r.at).toLocaleDateString()}{r.linked_version ? ` · ${r.linked_version}` : ""}
                  {r.note && <p className="text-mill-muted">{r.note}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AddSource({ config, onClose, onCreated }) {
  const [f, setF] = useState({ title: "", obj_type: config?.obj_types?.[0] || "Internal Mill policy", scope: "internal", owner: "", version: "v1.0", status: "current", canonical_url: "", source_org: "", review_due_date: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.title || !f.owner) { toast.error("Title and owner are required."); return; }
    setBusy(true);
    try { await post("/desk/sources", f); toast.success("Object added."); onCreated(); }
    catch { toast.error("Could not add object."); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="add-source-dialog">
        <DialogHeader><DialogTitle className="font-display text-lg">Add a governed object</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><Label className="text-xs text-mill-muted">Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="add-title" /></div>
          <div><Label className="text-xs text-mill-muted">Type</Label>
            <select className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={f.obj_type} onChange={(e) => setF({ ...f, obj_type: e.target.value })} data-testid="add-type">
              {config?.obj_types.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div><Label className="text-xs text-mill-muted">Scope</Label>
            <select className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })} data-testid="add-scope">
              <option value="internal">Internal</option><option value="external">External</option>
            </select>
          </div>
          <div><Label className="text-xs text-mill-muted">Owner</Label><Input value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} data-testid="add-owner" /></div>
          <div><Label className="text-xs text-mill-muted">Version</Label><Input value={f.version} onChange={(e) => setF({ ...f, version: e.target.value })} data-testid="add-version" /></div>
          {f.scope === "external" && <>
            <div className="sm:col-span-2"><Label className="text-xs text-mill-muted">Canonical URL</Label><Input value={f.canonical_url} onChange={(e) => setF({ ...f, canonical_url: e.target.value })} placeholder="https://…" data-testid="add-url" /></div>
            <div className="sm:col-span-2"><Label className="text-xs text-mill-muted">Primary source organisation</Label><Input value={f.source_org} onChange={(e) => setF({ ...f, source_org: e.target.value })} data-testid="add-org" /></div>
          </>}
          <div><Label className="text-xs text-mill-muted">Review due date</Label><Input type="date" value={f.review_due_date} onChange={(e) => setF({ ...f, review_due_date: e.target.value })} data-testid="add-review-due" /></div>
        </div>
        <Button className="mt-2 bg-mill-moss hover:bg-mill-moss-dark" onClick={submit} disabled={busy} data-testid="add-submit">Add object</Button>
      </DialogContent>
    </Dialog>
  );
}
